/**
 * Styles for the redesigned report layout: the verdict band, the needs-
 * attention block, the enriched test row and the detail section rail.
 *
 * A separate file because `html-css.ts` is already 569 lines, past the
 * 500-line ceiling in Constitution Principle II. Everything here uses the
 * tokens defined there — no new colours, radii or type sizes are introduced.
 */

export const CSS_LAYOUT = `
/* ── Wider shell ──
   960px was sized for a list. The row now carries evidence and timing, and
   the detail panel shows two screenshots side by side; both were cramped. */
.wrap{max-width:1400px;padding:28px 40px 64px}

/* ── Verdict band (replaces the six equal stat chips) ── */
.verdict{display:flex;gap:16px;align-items:stretch;margin-bottom:22px}
.verdict-main{flex:1;min-width:0;background:var(--bg-1);border:1px solid var(--bd);border-radius:12px;padding:18px 22px;display:flex;flex-direction:column;gap:14px}
.verdict-main.v-failed{border-color:rgba(239,68,68,0.22)}
.verdict-main.v-passed{border-color:rgba(34,197,94,0.2)}
.verdict-head{display:flex;align-items:baseline;gap:12px;flex-wrap:wrap}
.verdict-title{font-size:21px;font-weight:700;letter-spacing:-0.01em;color:var(--fg)}
.verdict-where{font-size:12px;color:var(--fg-1)}
.verdict-where code{font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;color:var(--fg)}
.verdict-dur{margin-left:auto;display:flex;align-items:center;gap:6px;font-size:12px;color:var(--fg-1);white-space:nowrap}

/* Proportion bar: one glance at the shape of the run. */
.verdict-bar{display:flex;height:8px;gap:2px}
.verdict-bar span{border-radius:999px;min-width:3px}
.vb-passed{background:#22c55e}.vb-failed{background:#ef4444}
.vb-flaky{background:#f59e0b}.vb-skipped{background:#6b7280}.vb-timedout{background:#f97316}
.verdict-bar-empty{flex:1;background:var(--bd-m);border-radius:999px}

.verdict-counts{display:flex;align-items:center;gap:20px;flex-wrap:wrap}
.vc{display:flex;align-items:center;gap:7px}
.vc-dot{width:9px;height:9px;border-radius:50%;flex-shrink:0}
.vc-n{font-size:14px;font-weight:700;color:var(--fg);font-variant-numeric:tabular-nums}
.vc-l{font-size:12px;color:var(--fg-1)}
/* Statuses with nothing in them stop taking a sixth of the width. */
.verdict-zeros{margin-left:auto;font-size:11px;color:var(--fg-2)}

.verdict-side{width:268px;flex-shrink:0;background:var(--bg-1);border:1px solid var(--bd);border-radius:12px;padding:18px 20px;display:flex;flex-direction:column;gap:12px}
.verdict-side-label{font-size:10px;font-weight:600;color:var(--fg-2);letter-spacing:.06em;text-transform:uppercase}
.verdict-side-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px 10px}
.vs-n{font-size:17px;font-weight:700;color:var(--fg);font-variant-numeric:tabular-nums}
.vs-n.vs-dom{color:#e879f9}
.vs-l{font-size:10px;color:var(--fg-1)}

/* ── Needs attention ── */
.needs{display:flex;flex-direction:column;gap:10px;margin-bottom:22px}
.needs-head{display:flex;align-items:center;gap:8px;padding:0 2px}
.needs-title{font-size:11px;font-weight:700;color:var(--fg);letter-spacing:.06em;text-transform:uppercase}
.needs-count{font-size:10px;font-weight:700;color:#ef4444;background:rgba(239,68,68,0.12);padding:1px 7px;border-radius:4px}
.needs-list{display:flex;flex-direction:column;gap:8px}
.needs-card{background:var(--bg-1);border:1px solid rgba(239,68,68,0.2);border-left:3px solid #ef4444;border-radius:10px;padding:14px 18px;display:flex;gap:16px;align-items:flex-start;cursor:pointer;transition:border-color .15s,background .1s}
.needs-card:hover{background:var(--hvr);border-color:rgba(239,68,68,0.4)}
.needs-card.nc-flaky{border-color:rgba(245,158,11,0.2);border-left-color:#f59e0b}
.needs-card.nc-flaky:hover{border-color:rgba(245,158,11,0.4)}
.needs-body{flex:1;min-width:0;display:flex;flex-direction:column;gap:8px}
.needs-row1{display:flex;align-items:center;gap:10px;flex-wrap:wrap}
.needs-name{font-size:14px;font-weight:600;color:var(--fg)}
.needs-file{font-size:11px;color:var(--fg-2);font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace}
.needs-cause{font-size:12px;color:var(--fg-1);font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;line-height:1.6;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.needs-cause em{font-style:normal;color:var(--fg-err)}
.needs-chips{display:flex;gap:6px;flex-wrap:wrap;align-items:center}
.needs-thumb{width:148px;flex-shrink:0;display:flex;flex-direction:column;gap:5px}
.needs-thumb img{width:100%;height:92px;object-fit:cover;object-position:top center;border-radius:7px;border:1px solid rgba(239,68,68,0.35);background:var(--bg)}
.needs-thumb-cap{font-size:9px;color:var(--fg-2);text-align:center}
.needs-right{display:flex;flex-direction:column;align-items:flex-end;gap:8px;flex-shrink:0}
.needs-dur{font-size:13px;font-weight:700;color:var(--fg-1);font-variant-numeric:tabular-nums}
.needs-open{display:flex;align-items:center;gap:4px;font-size:11px;font-weight:600;color:#2dd4a8}

/* Pill chips used in the needs cards. */
.nchip{display:flex;align-items:center;gap:4px;font-size:10px;font-weight:500;color:var(--fg-1);background:var(--bg-2);border:1px solid var(--bd);padding:2px 8px;border-radius:999px}
.nchip-visual{color:#2dd4a8;background:rgba(3,183,156,0.1);border-color:rgba(3,183,156,0.22);font-weight:600}
.nchip-dom{color:#e879f9;background:rgba(232,121,249,0.1);border-color:rgba(232,121,249,0.2);font-weight:600}

/* ── Enriched test row ──
   Same height as before. Two regions added: what was captured, and how long
   it took relative to the slowest test in the run. */
.trow-evidence{display:flex;gap:4px;align-items:center;flex-shrink:0}
.tev{width:18px;height:18px;border-radius:4px;background:var(--bg-2);display:flex;align-items:center;justify-content:center;color:var(--fg-1);flex-shrink:0}
.tev-visual{background:rgba(3,183,156,0.12);color:#2dd4a8}
.tev-dom{background:rgba(232,121,249,0.12);color:#e879f9}
.tev-err{background:rgba(239,68,68,0.12);color:#ef4444}
.trow-time{width:120px;flex-shrink:0;display:flex;align-items:center;gap:8px}
.trow-bar{flex:1;height:4px;border-radius:999px;background:var(--bd-m);overflow:hidden}
.trow-bar span{display:block;height:100%;border-radius:999px}
.tb-passed{background:#22c55e}.tb-failed{background:#ef4444}
.tb-flaky{background:#f59e0b}.tb-skipped{background:#6b7280}.tb-timedout{background:#f97316}
.test-row-dur{font-variant-numeric:tabular-nums}
/* The title and its badges sit on one line now that the row has columns. */
.test-row-info.tri-inline{flex-direction:row;align-items:center;gap:10px}
.test-row-info.tri-inline .test-row-badges{flex-shrink:0}

/* ── Detail panel ──
   Was 50vw, which left the comparison about 330px a side and a dead half-
   screen of grid behind it. */
.drawer{width:min(1240px,94vw);max-width:94vw}
.drawer-layout{display:flex;gap:28px;align-items:flex-start}
.drawer-rail{width:206px;flex-shrink:0;position:sticky;top:0;display:flex;flex-direction:column;gap:18px}
.drawer-main{flex:1;min-width:0;display:flex;flex-direction:column;gap:24px}
.rail-group{display:flex;flex-direction:column;gap:2px}
.rail-label{font-size:10px;font-weight:600;color:var(--fg-2);letter-spacing:.06em;text-transform:uppercase;padding:0 10px 7px}
.rail-item{display:flex;align-items:center;gap:9px;padding:8px 10px;border-radius:7px;color:var(--fg-1);cursor:pointer;transition:background .12s,color .12s;border:none;background:transparent;font-family:inherit;text-align:left;width:100%}
.rail-item:hover{background:var(--bd-xs);color:var(--fg)}
.rail-item.active{background:rgba(3,183,156,0.12);color:#2dd4a8}
.rail-item.active .rail-name{font-weight:600}
.rail-item.rail-fail{color:var(--fg-err)}
.rail-item.rail-fail.active{background:rgba(239,68,68,0.1);color:var(--fg-err)}
.rail-name{font-size:12.5px;font-weight:500;flex:1;min-width:0}
.rail-n{font-size:10px;font-weight:700;background:var(--bd-xs);padding:1px 6px;border-radius:4px;font-variant-numeric:tabular-nums}
.rail-item.active .rail-n{background:rgba(3,183,156,0.18)}
.rail-n.rail-n-dom{color:#e879f9;background:rgba(232,121,249,0.12)}
.rail-meta{border-top:1px solid var(--bd);padding-top:16px;display:flex;flex-direction:column;gap:9px}
.rail-meta-rows{padding:0 10px;display:flex;flex-direction:column;gap:7px;font-size:11px}
.rail-meta-row{display:flex;gap:8px}
.rail-meta-k{color:var(--fg-2);width:44px;flex-shrink:0}
.rail-meta-v{color:var(--fg-1);min-width:0;overflow:hidden;text-overflow:ellipsis}
.rail-meta-v.mono{font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace}

/* Scroll-margin so a rail jump does not tuck the heading under the bar. */
.drawer-section{scroll-margin-top:12px}

@media (max-width:1100px){
  .drawer{width:96vw;max-width:96vw}
  .drawer-layout{flex-direction:column;gap:18px}
  .drawer-rail{width:100%;position:static;flex-direction:row;flex-wrap:wrap;gap:6px}
  .rail-group{flex-direction:row;flex-wrap:wrap;gap:4px}
  .rail-label,.rail-meta{display:none}
  .rail-item{width:auto}
  .verdict{flex-direction:column}
  .verdict-side{width:100%}
}
`;

/**
 * Styles for the detail page.
 *
 * The detail used to be a fixed overlay; it is now a view in the flow, so the
 * page scrolls rather than a panel inside it. The `.drawer-*` class names are
 * kept because four modules query them (`.drawer-body video`, the section
 * wrappers) — the names are historical, the layout is not.
 */
export const CSS_DETAIL_PAGE = `
/* The overlay is gone. Anything still carrying .drawer must not float. */
.drawer,.drawer-backdrop{display:none}

.detail-page{display:flex;flex-direction:column;gap:20px}
#view-detail[hidden],#view-overview[hidden]{display:none}

.detail-nav{display:flex;align-items:center;gap:14px;flex-wrap:wrap;padding-bottom:14px;border-bottom:1px solid var(--bd)}
.detail-back{display:inline-flex;align-items:center;gap:6px;font-size:12px;font-weight:600;color:var(--fg-1);background:transparent;border:1px solid var(--bd-m);border-radius:8px;padding:6px 12px 6px 9px;cursor:pointer;font-family:inherit;transition:background .15s,color .15s,border-color .15s}
.detail-back:hover{background:var(--hvr);color:var(--fg);border-color:var(--bd-l)}
.detail-crumb{font-size:11px;color:var(--fg-2);font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace}

.detail-steps{margin-left:auto;display:inline-flex;align-items:center;border:1px solid var(--bd-m);border-radius:8px;overflow:hidden}
.detail-step{display:flex;align-items:center;justify-content:center;width:32px;height:30px;background:transparent;border:none;color:var(--fg-1);cursor:pointer;font-family:inherit;transition:background .15s,color .15s}
.detail-step:hover:not(.is-off){background:var(--hvr);color:var(--fg)}
.detail-step.is-off{color:var(--fg-2);cursor:default}
.detail-pos{font-size:11px;font-weight:600;color:var(--fg-1);padding:0 10px;border-left:1px solid var(--bd-m);border-right:1px solid var(--bd-m);line-height:30px;font-variant-numeric:tabular-nums}

/* The page scrolls now, so the old panel body must not scroll inside it. */
#view-detail .drawer-body{flex:none;overflow:visible;padding:0}
.detail-page .detail-rail,.detail-page .drawer-rail{top:16px}
`;
